import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Tabs,
  Tab,
  Paper,
  Divider,
  IconButton,
  FormHelperText,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const weekDays = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const slotTypes = [
  { label: "Class", value: "class" },
  { label: "Break", value: "break" },
  { label: "Short Break", value: "short break" },
  { label: "Exam", value: "exam" },
];

const emptySlot = {
  startTime: "",
  endTime: "",
  slotType: "",
};

const createDays = () =>
  weekDays.map((day) => ({
    day,
    status: "",
    reason: "",
    sameAsDay: "",
    slots: [{ ...emptySlot }],
  }));

const ManageTimeTableForm = ({
  initialData,
  onSubmit,
  onCancel,
  buttonLoading,
  academicYears = {},
  classSections = [],
}) => {
  const [activeDay, setActiveDay] = useState(0);

  const [form, setForm] = useState({
    academicYearId: "",
    classSectionId: "",
    days: createDays(),
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setForm({
        academicYearId: initialData.academicYearId || "",
        classSectionId: initialData.classSectionId || "",
        days: initialData.days?.length
          ? initialData.days.map((d) => ({
              sameAsDay: d.sameAsDay || "",
              ...d,
            }))
          : createDays(),
        ...(initialData.timeTableId && {
          timeTableId: initialData.timeTableId,
        }),
      });
    }
  }, [initialData]);

  const handleField = (name, value) => {
    const updatedForm = { ...form, [name]: value };
    setForm(updatedForm);

    const temp = { ...errors };

    if (name === "academicYearId") {
      if (!value) temp.academicYearId = "Academic Year is required";
      else delete temp.academicYearId;
    }

    if (name === "classSectionId") {
      if (!value) temp.classSectionId = "Class Section is required";
      else delete temp.classSectionId;
    }

    setErrors(temp);
  };

  const updateDay = (dayIndex, key, value) => {
    const updatedDays = [...form.days];
    updatedDays[dayIndex][key] = value;

    if (key === "status" && value === "holiday") {
      updatedDays[dayIndex].slots = [];
    }

    if (
      key === "status" &&
      value === "working" &&
      updatedDays[dayIndex].slots.length === 0
    ) {
      updatedDays[dayIndex].slots = [{ ...emptySlot }];
    }

    setForm({ ...form, days: updatedDays });

    const temp = { ...errors };

    if (key === "status") {
      if (!value) temp[`status-${dayIndex}`] = "Status is required";
      else delete temp[`status-${dayIndex}`];
    }

    if (key === "reason") {
      if (updatedDays[dayIndex].status === "holiday" && !value.trim()) {
        temp[`reason-${dayIndex}`] = "Reason is required";
      } else {
        delete temp[`reason-${dayIndex}`];
      }
    }

    setErrors(temp);
  };

  const updateSlot = (dayIndex, slotIndex, key, value) => {
    const updatedDays = [...form.days];
    updatedDays[dayIndex].slots[slotIndex][key] = value;

    const slot = updatedDays[dayIndex].slots[slotIndex];

    setForm({ ...form, days: updatedDays });

    const temp = { ...errors };

    if (key === "startTime") {
      if (!value) temp[`start-${dayIndex}-${slotIndex}`] = "Required";
      else delete temp[`start-${dayIndex}-${slotIndex}`];
    }

    if (key === "endTime") {
      if (!value) {
        temp[`end-${dayIndex}-${slotIndex}`] = "Required";
      } else if (slot.startTime && value <= slot.startTime) {
        temp[`end-${dayIndex}-${slotIndex}`] = "End time must be greater";
      } else {
        delete temp[`end-${dayIndex}-${slotIndex}`];
      }
    }

    if (key === "slotType") {
      if (!value) temp[`type-${dayIndex}-${slotIndex}`] = "Required";
      else delete temp[`type-${dayIndex}-${slotIndex}`];
    }

    if (slot.startTime && slot.endTime && slot.endTime <= slot.startTime) {
      temp[`end-${dayIndex}-${slotIndex}`] = "End time must be greater";
    }

    setErrors(temp);
  };

  const handleSameAsDay = (dayIndex, sourceDay) => {
    const updatedDays = [...form.days];

    if (!sourceDay) {
      updatedDays[dayIndex].sameAsDay = "";
    } else {
      const source = updatedDays.find((item) => item.day === sourceDay);

      updatedDays[dayIndex] = {
        ...updatedDays[dayIndex],
        sameAsDay: sourceDay,
        status: source.status,
        reason: source.reason,
        slots: source.slots.map((slot) => ({ ...slot })),
      };
    }

    setForm({ ...form, days: updatedDays });
  };

  const addSlot = (dayIndex) => {
    const updated = [...form.days];
    updated[dayIndex].slots.push({ ...emptySlot });

    setForm((prev) => ({
      ...prev,
      days: updated,
    }));
  };

  const removeSlot = (dayIndex, slotIndex) => {
    const updated = [...form.days];
    updated[dayIndex].slots.splice(slotIndex, 1);

    if (updated[dayIndex].slots.length === 0) {
      updated[dayIndex].slots.push({ ...emptySlot });
    }

    setForm((prev) => ({
      ...prev,
      days: updated,
    }));
  };

  const validate = () => {
    const temp = {};

    if (!form.academicYearId) {
      temp.academicYearId = "Academic Year is required";
    }

    if (!form.classSectionId) {
      temp.classSectionId = "Class Section is required";
    }

    form.days.forEach((day, dayIndex) => {
      if (!day.status) {
        temp[`status-${dayIndex}`] = "Status is required";
      }

      if (day.status === "holiday" && !day.reason.trim()) {
        temp[`reason-${dayIndex}`] = "Reason is required";
      }

      if (day.status === "working") {
        day.slots.forEach((slot, slotIndex) => {
          if (!slot.startTime) {
            temp[`start-${dayIndex}-${slotIndex}`] = "Required";
          }

          if (!slot.endTime) {
            temp[`end-${dayIndex}-${slotIndex}`] = "Required";
          }

          if (!slot.slotType) {
            temp[`type-${dayIndex}-${slotIndex}`] = "Required";
          }

          if (
            slot.startTime &&
            slot.endTime &&
            slot.endTime <= slot.startTime
          ) {
            temp[`end-${dayIndex}-${slotIndex}`] = "End time must be greater";
          }
        });
      }
    });

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    onSubmit(form);
  };

  const selectedDay = form.days[activeDay];

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        maxHeight: "85vh",
        overflowY: "auto",
        pr: 1,
      }}
    >
      <Typography variant="h6" fontWeight={600} textAlign="center" mb={3}>
        {initialData ? "Update Timetable" : "Create Timetable"}
      </Typography>

      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Academic Year *"
          value={form.academicYearId}
          onChange={(e) => handleField("academicYearId", e.target.value)}
          error={!!errors.academicYearId}
          helperText={errors.academicYearId}
          sx={{ textTransform: "capitalize" }}
        >
          {!academicYears?.academicYearId ? (
            <MenuItem disabled>No data available</MenuItem>
          ) : (
            <MenuItem
              value={academicYears.academicYearId}
              sx={{ textTransform: "capitalize" }}
            >
              {academicYears.name}
            </MenuItem>
          )}
        </TextField>

        <TextField
          select
          fullWidth
          size="small"
          label="Class Section *"
          value={form.classSectionId}
          onChange={(e) => handleField("classSectionId", e.target.value)}
          error={!!errors.classSectionId}
          helperText={errors.classSectionId}
          sx={{ textTransform: "capitalize" }}
        >
          {classSections.length === 0 ? (
            <MenuItem disabled>No data available</MenuItem>
          ) : (
            classSections.map((item) => (
              <MenuItem
                key={item.classSectionId}
                value={item.classSectionId}
                sx={{ textTransform: "capitalize" }}
              >
                {item.title}
              </MenuItem>
            ))
          )}
        </TextField>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          mt: 3,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeDay}
          onChange={(e, value) => setActiveDay(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {weekDays.map((day) => (
            <Tab key={day} label={day} sx={{ textTransform: "capitalize" }} />
          ))}
        </Tabs>

        <Divider />

        <Box
          sx={{
            p: 2,
            maxHeight: "170px",
            overflowY: "auto",
          }}
        >
          <TextField
            select
            fullWidth
            size="small"
            label="Same As Day"
            value={selectedDay.sameAsDay || ""}
            onChange={(e) => handleSameAsDay(activeDay, e.target.value)}
            sx={{ mb: 2, textTransform: "capitalize" }}
          >
            <MenuItem value="">None</MenuItem>

            {weekDays
              .filter((d) => d !== selectedDay.day)
              .map((day) => (
                <MenuItem key={day} value={day} sx={{ textTransform: "capitalize" }}>
                  {day}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            select
            fullWidth
            size="small"
            label="Status *"
            value={selectedDay.status}
            onChange={(e) => updateDay(activeDay, "status", e.target.value)}
            error={!!errors[`status-${activeDay}`]}
            helperText={errors[`status-${activeDay}`]}
            sx={{ mb: 2 }}
          >
            <MenuItem value="working">Working</MenuItem>
            <MenuItem value="holiday">Holiday</MenuItem>
          </TextField>

          {selectedDay.status === "holiday" && (
            <TextField
              fullWidth
              size="small"
              label="Reason *"
              value={selectedDay.reason}
              onChange={(e) => updateDay(activeDay, "reason", e.target.value)}
              error={!!errors[`reason-${activeDay}`]}
              helperText={errors[`reason-${activeDay}`]}
            />
          )}

          {selectedDay.status === "working" && (
            <>
              {selectedDay.slots.map((slot, slotIndex) => (
                <Box
                  key={slotIndex}
                  display="grid"
                  gridTemplateColumns="1fr 1fr 1fr auto"
                  gap={2}
                  mb={2}
                >
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      type="time"
                      label="Start Time *"
                      value={slot.startTime}
                      onChange={(e) =>
                        updateSlot(
                          activeDay,
                          slotIndex,
                          "startTime",
                          e.target.value,
                        )
                      }
                      slotProps={{
                        inputLabel: { shrink: true },
                      }}
                    />
                    <FormHelperText error>
                      {errors[`start-${activeDay}-${slotIndex}`]}
                    </FormHelperText>
                  </Box>

                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      type="time"
                      label="End Time *"
                      value={slot.endTime}
                      onChange={(e) =>
                        updateSlot(
                          activeDay,
                          slotIndex,
                          "endTime",
                          e.target.value,
                        )
                      }
                      slotProps={{
                        inputLabel: { shrink: true },
                      }}
                    />
                    <FormHelperText error>
                      {errors[`end-${activeDay}-${slotIndex}`]}
                    </FormHelperText>
                  </Box>

                  <Box>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Slot Type *"
                      value={slot.slotType}
                      onChange={(e) =>
                        updateSlot(
                          activeDay,
                          slotIndex,
                          "slotType",
                          e.target.value,
                        )
                      }
                    >
                      {slotTypes.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <FormHelperText error>
                      {errors[`type-${activeDay}-${slotIndex}`]}
                    </FormHelperText>
                  </Box>

                  <Box>
                    <IconButton
                      color="error"
                      onClick={() => removeSlot(activeDay, slotIndex)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              <Button
                variant="outlined"
                size="small"
                onClick={() => addSlot(activeDay)}
              >
                Add Slot
              </Button>
            </>
          )}
        </Box>
      </Paper>

      <Box mt={3} display="flex" justifyContent="flex-end" gap={1.5}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
          }}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="contained"
          loading={buttonLoading}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            backgroundColor: "rgb(35, 71, 159)",
          }}
        >
          {initialData ? "Update" : "Create"}
        </Button>
      </Box>
    </Box>
  );
};

export default ManageTimeTableForm;
